import React from "react";
import { IDSBadge, IDSCard, IDSTextarea } from "@inera/ids-react";
import SubmissionCommentThread from "./SubmissionCommentThread";
import QuestionItem from "./QuestionItem";
import { sectionAnchorId } from "./SectionNavigator";
import LinkifiedText, { buildLinkifiedNodes } from "../common/LinkifiedText";

const READONLY_TEXTAREA_STYLE = {
  color: "var(--ids-palette-neutral-20, #1e1e1e)",
  backgroundColor: "var(--ids-palette-neutral-100, #ffffff)",
  opacity: 1,
  cursor: "default",
  minHeight: "11rem",
  height: "11rem",
  resize: "vertical",
};

/** Small labelled box used for follow-up answers — same look as the review pages. */
const AnswerNote = ({ label, text, accent }) => (
  <div
    style={{
      marginTop: "0.6rem",
      borderLeft: `3px solid ${accent}`,
      background: "var(--ids-palette-neutral-97, #f6f6f6)",
      padding: "0.5rem 0.8rem",
      borderRadius: "4px",
    }}
  >
    <span style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "var(--ids-palette-neutral-40)" }}>
      {label}
    </span>
    <span style={{ fontSize: "0.9rem", whiteSpace: "pre-wrap" }}>{text}</span>
  </div>
);

const FollowUpAnswer = ({ followUpQuestion }) => {
  if (!followUpQuestion) return null;
  return (
    <AnswerNote
      label={followUpQuestion.questionText || "Följdfråga"}
      text={followUpQuestion.answerText || "(Inget svar angivet)"}
      accent="#0065bd"
    />
  );
};

/** Read-only rendering of a single submitted answer, by question type. */
const QuestionAnswer = ({ question: q }) => {
  const hasFreeText = q.answerText != null && String(q.answerText).trim() !== "";
  const hasSingleChoice = !!q.selectedOption;
  const hasMultiChoice = Array.isArray(q.selectedOptions) && q.selectedOptions.length > 0;

  if (hasSingleChoice) {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
          <IDSBadge type="info">{q.selectedOption.optionText}</IDSBadge>
        </div>
        <FollowUpAnswer followUpQuestion={q.selectedOption.followUpQuestion} />
      </div>
    );
  }

  if (hasMultiChoice) {
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
          {q.selectedOptions.map((opt) => (
            <IDSBadge key={opt.optionId} type="info">{opt.optionText}</IDSBadge>
          ))}
        </div>
        {q.selectedOptions.map((opt) => (
          <FollowUpAnswer key={`fu-${opt.optionId}`} followUpQuestion={opt.followUpQuestion} />
        ))}
      </div>
    );
  }

  if (hasFreeText) {
    return <IDSTextarea block light readOnly value={q.answerText} rows={4} style={READONLY_TEXTAREA_STYLE} />;
  }

  return <IDSBadge type="neutral">Ej besvarad</IDSBadge>;
};

export default function SubmissionSectionTreeWithComments({
  sections = [],
  submissionId,
  submissionUserId,
  authorName,
  allComments,
  onCommentAdded,
  canAddComments = true,
  requireExistingComments = false,
  editableQuestionIds,
  answers = {},
  onAnswerChange,
  validationErrors,
}) {
  return sections.map((section) => (
    <Section
      key={section.sectionId}
      section={section}
      level={0}
      submissionId={submissionId}
      submissionUserId={submissionUserId}
      authorName={authorName}
      allComments={allComments}
      onCommentAdded={onCommentAdded}
      canAddComments={canAddComments}
      requireExistingComments={requireExistingComments}
      editableQuestionIds={editableQuestionIds}
      answers={answers}
      onAnswerChange={onAnswerChange}
      validationErrors={validationErrors}
    />
  ));
}

function Section({
  section,
  level = 0,
  submissionId,
  submissionUserId,
  authorName,
  allComments,
  onCommentAdded,
  canAddComments,
  requireExistingComments,
  editableQuestionIds,
  answers = {},
  onAnswerChange,
  validationErrors,
}) {
  const isEditable = (questionId) =>
    !!onAnswerChange && !!editableQuestionIds && editableQuestionIds.has(questionId);

  const {
    sectionId,
    sectionCode,
    sectionTitle,
    sectionDescription,
    questions = [],
    children = [],
  } = section;

  const commentsForAnswer = (answerId) =>
    (Array.isArray(allComments) ? allComments : []).filter(
      (c) => (c.answerId ?? c.answer_id) === answerId
    );

  const body = (
    <>
      <h3 className={level === 0 ? "ids-heading-m" : "ids-heading-s"} style={{ marginTop: 0 }}>
        {sectionCode} {sectionTitle}
      </h3>

      <LinkifiedText
        className="ids-body ids-mb-4"
        style={{ whiteSpace: "pre-line", color: "var(--ids-palette-neutral-30)" }}
      >
        {sectionDescription}
      </LinkifiedText>

      {questions.map((q, idx) => (
        <div key={q.questionId} style={{ marginBottom: "1.5rem" }}>
          {isEditable(q.questionId) ? (
            <QuestionItem
              question={q}
              index={idx + 1}
              value={answers[q.questionId] ?? ""}
              answers={answers}
              onAnswerChange={onAnswerChange}
              validationErrors={validationErrors}
            />
          ) : (
            <>
              <p style={{ fontWeight: 600, fontSize: "0.95rem", marginBottom: "0.2rem", whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
                {idx + 1}. {buildLinkifiedNodes(q.questionText)}
              </p>

              <LinkifiedText
                className="ids-body"
                style={{ marginBottom: "0.4rem", fontSize: "0.875rem", color: "var(--ids-palette-neutral-30)", whiteSpace: "pre-line" }}
              >
                {q.questionDescription}
              </LinkifiedText>

              <QuestionAnswer question={q} />
            </>
          )}

          {(() => {
            const questionComments = commentsForAnswer(q.answerId);
            // In the clarification round the tester may only comment where the
            // reviewer already has — elsewhere the thread is hidden entirely.
            if (!q.answerId || (requireExistingComments && questionComments.length === 0)) return null;
            return (
              <SubmissionCommentThread
                submissionId={submissionId}
                submissionUserId={submissionUserId}
                authorName={authorName}
                answerId={q.answerId}
                comments={questionComments}
                canAdd={canAddComments}
                onCommentAdded={onCommentAdded}
              />
            );
          })()}
        </div>
      ))}

      {children.map((child) => (
        <Section
          key={child.sectionId}
          section={child}
          level={level + 1}
          submissionId={submissionId}
          submissionUserId={submissionUserId}
          authorName={authorName}
          allComments={allComments}
          onCommentAdded={onCommentAdded}
          canAddComments={canAddComments}
          requireExistingComments={requireExistingComments}
          editableQuestionIds={editableQuestionIds}
          answers={answers}
          onAnswerChange={onAnswerChange}
          validationErrors={validationErrors}
        />
      ))}
    </>
  );

  // Top-level sections get their own card and an anchor for the section navigator.
  if (level === 0) {
    return (
      <IDSCard className="ids-mt-4" id={sectionAnchorId(sectionId)}>
        {body}
      </IDSCard>
    );
  }

  return (
    <div className="ids-mb-6" style={{ marginLeft: `${level * 24}px` }}>
      {body}
    </div>
  );
}
